import { useCallback, useEffect, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { useMutation } from '@tanstack/react-query';
import { Upload, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogBody, DialogFooter, DialogClose } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { uploadImage } from '@/lib/adminApi';
import { CF_IMAGES_ACCOUNT_HASH } from '@/lib/supabase';
import { cfImageUrl } from '@/lib/utils';

interface AvatarCropperDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the final CF Images delivery URL (or with the new image id +
   *  url separated if the parent needs both). */
  onUploaded: (avatarUrl: string) => void;
}

// Instagram-style avatar editor: pick a file → drag + zoom inside a circular
// crop frame → save. We render the source image full quality, crop locally
// with a canvas, then POST the blob to CF Images via the admin worker so the
// service token never touches the browser.
//
// Output is a square PNG at 512×512 (plenty for avatar use, ~50-80 KB).
// CF Images applies its own variants on delivery so the same image scales
// down for thumbnails automatically.
const OUTPUT_SIZE = 512;

export function AvatarCropperDialog({ open, onOpenChange, onUploaded }: AvatarCropperDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  // Reset state every time the dialog closes so reopening starts clean.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [open]);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      alert('Please pick an image file.');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(f);
  };

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const upload = useMutation({
    mutationFn: async () => {
      if (!imageSrc || !croppedAreaPixels) throw new Error('No crop area');
      const blob = await renderCroppedBlob(imageSrc, croppedAreaPixels);
      const filename = (file?.name?.replace(/\.[^.]+$/, '') ?? 'avatar') + '.png';
      const imageId = await uploadImage(new File([blob], filename, { type: 'image/png' }));
      const url = cfImageUrl(imageId, CF_IMAGES_ACCOUNT_HASH, 'public') ?? '';
      if (!url) throw new Error('CF_IMAGES_ACCOUNT_HASH not configured');
      return url;
    },
    onSuccess: (url) => {
      toast.success('Avatar uploaded');
      onUploaded(url);
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !upload.isPending && onOpenChange(v)}>
      <DialogContent className="max-w-md">
        <div className="px-6 pt-5">
          <DialogTitle>Change avatar</DialogTitle>
          <DialogDescription>Pick, position, zoom.</DialogDescription>
        </div>

        <DialogBody className="space-y-4">
          {!imageSrc ? (
            <FilePicker onChange={onSelectFile} />
          ) : (
            <div className="space-y-3">
              <div className="relative h-72 w-full bg-bg-elevated rounded-md overflow-hidden">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  classes={{ containerClassName: 'bg-bg-elevated' }}
                />
              </div>
              <div className="flex items-center gap-3">
                <ZoomOut className="h-4 w-4 text-fg-muted shrink-0" />
                <input
                  type="range"
                  min={1}
                  max={4}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full accent-thermal-orange"
                  aria-label="Zoom"
                />
                <ZoomIn className="h-4 w-4 text-fg-muted shrink-0" />
              </div>
              <button
                type="button"
                onClick={() => { setImageSrc(null); setFile(null); }}
                className="text-xs text-fg-muted hover:text-fg underline underline-offset-2"
              >
                Pick a different image
              </button>
            </div>
          )}

          {upload.error ? (
            <div className="rounded-md border border-thermal-red/30 bg-thermal-red/5 p-2 text-xs text-thermal-red">
              {(upload.error as Error).message}
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" type="button" disabled={upload.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            onClick={() => upload.mutate()}
            disabled={!imageSrc || !croppedAreaPixels || upload.isPending}
          >
            {upload.isPending ? 'Uploading…' : 'Save avatar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilePicker({ onChange }: { onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) {
  return (
    <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-border rounded-md py-12 cursor-pointer hover:bg-bg-elevated transition-colors">
      <Upload className="h-6 w-6 text-fg-muted" />
      <span className="text-sm text-fg-muted">Click to pick an image</span>
      <span className="text-xs text-fg-faint">PNG, JPEG, GIF, WebP / any size</span>
      <input type="file" accept="image/*" className="sr-only" onChange={onChange} />
      <span className="sr-only"><Label>Pick image</Label></span>
    </label>
  );
}

// ─────────────── Canvas crop helper ───────────────

// react-easy-crop returns the crop area in source-image pixels. We draw that
// region into a square canvas at OUTPUT_SIZE and export as PNG. The "round"
// cropShape in the UI is just a visual mask / the exported PNG is still
// square, which is what CF Images wants. The avatar component on display
// applies its own border-radius:50%.
async function renderCroppedBlob(imageSrc: string, area: Area): Promise<Blob> {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2d context');
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png', 0.95);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}
